import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tag, Plus, Trash2, Percent, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function PromoCodesPage() {
  const [codes, setCodes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    code: '', description: '', discount_type: 'percent',
    discount_value: 10, min_order_amount: 0, max_uses: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const r = await axios.get(`${API}/promo-codes`);
    setCodes(r.data);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/promo-codes`, form);
      toast.success('Промокод создан');
      setDialogOpen(false);
      setForm({ code: '', description: '', discount_type: 'percent', discount_value: 10, min_order_amount: 0, max_uses: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить промокод?')) return;
    await axios.delete(`${API}/promo-codes/${id}`);
    fetchData();
    toast.success('Удалено');
  };

  return (
    <div className="space-y-6" data-testid="promo-codes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Промокоды</h1>
          <p className="text-gray-500 mt-1">Скидочные коды для клиентов</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="add-promo-btn">
              <Plus className="h-4 w-4 mr-2" /> Создать промокод
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Новый промокод</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Код (заглавными)</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                  placeholder="NEWYEAR2026"
                  data-testid="promo-code-input"
                />
              </div>
              <div>
                <Label>Описание</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Скидка на Новый Год"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Тип скидки</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({...form, discount_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">% Процент</SelectItem>
                      <SelectItem value="fixed">₩ Фикс. сумма</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Размер</Label>
                  <Input
                    type="number"
                    value={form.discount_value}
                    onChange={(e) => setForm({...form, discount_value: parseFloat(e.target.value) || 0})}
                    data-testid="promo-value-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Мин. заказ (₩)</Label>
                  <Input
                    type="number"
                    value={form.min_order_amount}
                    onChange={(e) => setForm({...form, min_order_amount: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Макс. использований</Label>
                  <Input
                    type="number"
                    placeholder="Безлимит"
                    onChange={(e) => setForm({...form, max_uses: parseInt(e.target.value) || null})}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Отмена</Button>
              <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-promo-btn">Создать</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {codes.length === 0 ? (
          <Card className="p-12 text-center col-span-full">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Промокодов пока нет</p>
          </Card>
        ) : codes.map(c => (
          <Card key={c.id} className="p-5 cff-hover-lift" data-testid={`promo-card-${c.code}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="bg-orange-50 p-2 rounded-md">
                  {c.discount_type === 'percent' ? <Percent className="h-4 w-4 text-[#556B2F]" /> : <DollarSign className="h-4 w-4 text-[#556B2F]" />}
                </div>
                <div>
                  <p className="font-bold font-mono text-lg">{c.code}</p>
                  <p className="text-xs text-gray-500">{c.description || 'Без описания'}</p>
                </div>
              </div>
              <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Скидка:</span>
                <span className="font-semibold cff-text-numeric">
                  {c.discount_type === 'percent' ? `${c.discount_value}%` : `₩${c.discount_value.toLocaleString()}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Мин. заказ:</span>
                <span className="cff-text-numeric">₩{c.min_order_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Использовано:</span>
                <span className="cff-text-numeric">{c.times_used}/{c.max_uses || '∞'}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
